import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './ProjectCreationModal.css';

/**
 * Project Creation Modal - Shows progress during project and service creation
 * Features hexagonal loader animation
 * Steps: 1-Generating Name, 1.5-Preparing AI Prompt, 2-Ready, 3-Creating Project, 4-Creating Frontend (webapp) / skipped (brainstorming), 5-Complete
 */
const ProjectCreationModal = ({
  isOpen,
  projectName,
  serviceName,
  step = 0, // 0=hidden, 1=generating name, 1.5=preparing AI prompt, 2=ready, 3=creating project, 4=creating frontend, 5=complete
  mode = 'code' // 'code' | 'brainstorming'
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="project-creation-modal-overlay">
      <div className="project-creation-modal-content">
        {/* Hexagonal Loader */}
        <div className="hexagon-loader-container">
          <div className="hexagon-loader">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <polygon 
                points="50 5 93.3 28.75 93.3 71.25 50 95 6.7 71.25 6.7 28.75" 
                className="hexagon-shape"
              />
            </svg>
          </div>
          <div className="hexagon-loader hexagon-loader-2">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <polygon 
                points="50 5 93.3 28.75 93.3 71.25 50 95 6.7 71.25 6.7 28.75" 
                className="hexagon-shape"
              />
            </svg>
          </div>
          <div className="hexagon-loader hexagon-loader-3">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <polygon 
                points="50 5 93.3 28.75 93.3 71.25 50 95 6.7 71.25 6.7 28.75" 
                className="hexagon-shape"
              />
            </svg>
          </div>
        </div>

        {/* Progress Messages */}
        <div className="creation-progress">
          {/* Step 1: Generate app name from prompt */}
          <div className={`progress-step ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`}>
            <div className="step-indicator">
              {step === 1 ? (
                <div className="creation-step-spinner" />
              ) : step > 1 ? (
                <svg className="checkmark" fill="none" height="20" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" width="20">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ) : (
                <div className="step-dot" />
              )}
            </div>
            <div className="step-text">
              <strong>Generate app name from prompt</strong>
              <span className="step-name">AI analyzing your description...</span>
            </div>
          </div>

          {/* Step 1.5: Preparing AI prompt for creation */}
          <div className={`progress-step ${step === 1.5 ? 'active' : step > 1.5 ? 'completed' : ''}`}>
            <div className="step-indicator">
              {step === 1.5 ? (
                <div className="creation-step-spinner" />
              ) : step > 1.5 ? (
                <svg className="checkmark" fill="none" height="20" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" width="20">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ) : (
                <div className="step-dot" />
              )}
            </div>
            <div className="step-text">
              <strong>Preparing AI prompt for creation</strong>
              <span className="step-name">Optimizing development instructions...</span>
            </div>
          </div>

          {/* Step 2: Ready for creating */}
          <div className={`progress-step ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`}>
            <div className="step-indicator">
              {step === 2 ? (
                <div className="creation-step-spinner" />
              ) : step > 2 ? (
                <svg className="checkmark" fill="none" height="20" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" width="20">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ) : (
                <div className="step-dot" />
              )}
            </div>
            <div className="step-text">
              <strong>Ready for creating</strong>
              <span className="step-name">{projectName || 'project'}</span>
            </div>
          </div>

          {/* Step 3: Creating project */}
          <div className={`progress-step ${step === 3 ? 'active' : step > 3 ? 'completed' : ''}`}>
            <div className="step-indicator">
              {step === 3 ? (
                <div className="creation-step-spinner" />
              ) : step > 3 ? (
                <svg className="checkmark" fill="none" height="20" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" width="20">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ) : (
                <div className="step-dot" />
              )}
            </div>
            <div className="step-text">
              <strong>Creating {projectName || 'project'}</strong>
              <span className="step-name">Setting up infrastructure...</span>
            </div>
          </div>

          {/* Step 4: Creating frontend (webapp only) */}
          {mode !== 'brainstorming' && <div className={`progress-step ${step === 4 ? 'active' : step > 4 ? 'completed' : ''}`}>
            <div className="step-indicator">
              {step === 4 ? (
                <div className="creation-step-spinner" />
              ) : step > 4 ? (
                <svg className="checkmark" fill="none" height="20" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" width="20">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ) : (
                <div className="step-dot" />
              )}
            </div>
            <div className="step-text">
              <strong>Creating {projectName || 'project'} frontend</strong>
              <span className="step-name">{serviceName || 'frontend-service'}</span>
            </div>
          </div>}

          {/* Step 5: All ready to flow */}
          <div className={`progress-step ${step === 5 ? 'active completed' : ''}`}>
            <div className="step-indicator">
              {step === 5 ? (
                <svg className="checkmark" fill="none" height="20" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" width="20">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ) : (
                <div className="step-dot" />
              )}
            </div>
            <div className="step-text">
              <strong>All ready to flow! 🚀</strong>
              <span className="step-name">Your project is ready</span>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

ProjectCreationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  projectName: PropTypes.string,
  serviceName: PropTypes.string,
  step: PropTypes.number, // 0=hidden, 1-5=steps
  mode: PropTypes.string // 'code' | 'brainstorming'
};

ProjectCreationModal.defaultProps = {
  projectName: 'New Project',
  serviceName: 'New Service',
  step: 0,
  mode: 'code'
};

export default ProjectCreationModal;
